import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowRight, MapPin, Check, Phone, Star, HelpCircle, Quote } from "lucide-react";
import { publicApi, mediaUrl } from "../lib/api";
import { getLocation, LOCATIONS } from "../data/locations";
import Seo, { breadcrumbSchema, faqSchema, logoUrl } from "../components/Seo";
import NotFound from "./NotFound";
import { Button } from "../components/ui/button";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "../components/ui/accordion";
import GoogleReviewsSection from "../components/GoogleReviewsSection";
import BookCallButton from "../components/BookCallButton";
import { getCountyCopy } from "../data/countyCopy";
import { OPENING_HOURS_SPEC } from "../lib/business";
import { xeroSlugForTown } from "../data/xeroLocations";
import { serviceSlugsForTown, SERVICES } from "../data/serviceLocations";
import AskCassieRelated from "../components/AskCassieRelated";

const DIFFERENTIATORS = [
  "Dedicated day-to-day contact",
  "Unlimited support, fixed monthly fees",
  "Xero Platinum Partner",
  "Quarterly strategic reviews",
];

// Universal client outcomes — the "More Than Numbers" promise (shared, not thin).
const OUTCOMES = [
  ["Clear financial visibility", "Accurate, up-to-date numbers so you always know exactly where your business stands."],
  ["Better cash flow control", "Understand and plan your cash flow with confidence, not guesswork."],
  ["Confident decisions", "Meaningful business insights that help you make sharper, faster decisions."],
  ["Less admin, more time", "We take the finance function off your plate so you can focus on running your business."],
  ["Proactive, year-round support", "A dedicated contact with a direct dial — not a once-a-year accountant."],
  ["A finance function that scales", "Support that grows alongside your business, from start-up to established firm."],
];

const siteOrigin = () => (typeof window !== "undefined" ? window.location.origin : "");

// Per-town sections: the services a town actually comes to us for, and the plain
// statements of where we are and who we suit. Towns without them render as before.
const TownSection = ({ s }) => (
  <section className="max-w-5xl mx-auto px-4 sm:px-6 py-10 md:py-12" data-testid={`location-section-${s.heading.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}>
    <h2 className="text-2xl md:text-3xl font-extrabold text-navy max-w-3xl">{s.heading}</h2>
    {(s.body || []).map((p, i) => (
      <p key={i} className="text-slate-600 text-lg leading-relaxed max-w-3xl mt-4">{p}</p>
    ))}
    {(s.items || []).length > 0 && (
      <div className="grid sm:grid-cols-2 gap-3 mt-7">
        {s.items.map((it) => (
          <Link key={it.to} to={it.to} data-testid={`location-item-${it.to.split("/").pop()}`}
            className="group bg-slate-50 rounded-xl border border-slate-200 p-5 hover:border-orange hover:-translate-y-0.5 transition-all">
            <span className="inline-flex items-center gap-2 font-display font-bold text-navy">
              {it.label} <ArrowRight size={15} className="text-orange opacity-0 group-hover:opacity-100 transition-opacity" />
            </span>
            {it.note && <p className="text-sm text-slate-600 leading-relaxed mt-1.5">{it.note}</p>}
          </Link>
        ))}
      </div>
    )}
    {(s.links || []).map((l) => (
      <Link key={l.to} to={l.to} data-testid={`location-link-${l.to.replace(/\//g, "")}`}
        className="inline-flex items-center gap-2 text-orange font-bold hover:underline mt-6">
        {l.label} <ArrowRight size={15} />
      </Link>
    ))}
  </section>
);

export default function LocationPage() {
  const { locationSlug } = useParams();
  const loc = getLocation(locationSlug);
  const [services, setServices] = useState([]);
  const [rating, setRating] = useState(null);
  const [testimonial, setTestimonial] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    publicApi.services().then(setServices).catch(() => {});
    publicApi.googleRating().then(setRating).catch(() => {});
    publicApi.townTestimonials()
      .then((all) => setTestimonial((all || {})[locationSlug] || null))
      .catch(() => setTestimonial(null));
  }, [locationSlug]);

  if (!loc) return <NotFound />;

  const localBusiness = {
    "@context": "https://schema.org",
    "@type": "AccountingService",
    name: `Cloud Accounting Support Services — ${loc.town}`,
    description: loc.metaDescription,
    url: `${siteOrigin()}/${loc.slug}`,
    logo: logoUrl(),
    telephone: "+44 1827 780550",
    email: "info@cass-online.co.uk",
    areaServed: [loc.town, ...loc.nearby],
    address: {
      "@type": "PostalAddress",
      streetAddress: "3 Pebble Close, Amington",
      addressLocality: "Tamworth",
      addressRegion: "Staffordshire",
      postalCode: "B77 4RD",
      addressCountry: "GB",
    },
    openingHoursSpecification: OPENING_HOURS_SPEC,
    // Reviews of CASS itself are intentionally NOT marked up here (visible Google
    // reviews remain on the page). This avoids self-serving review-snippet markup.
  };

  const townFaqs = (loc.faqs || []).filter((f) => f && f.question);
  const sections = loc.sections || [];
  const heroPara = loc.opening || loc.intro[0];
  const bodyParas = loc.opening ? loc.intro : loc.intro.slice(1);
  const SHARED_LOC_FAQS = [
    { question: `How much does an accountant cost in ${loc.town}?`, answer: `Our pricing is published, so you don't have to ask. Build your package on our pricing page and you'll see your own monthly fee and any one-off costs in about two minutes, without giving us your email address. The figure comes from your own numbers — invoices raised, bank accounts to reconcile, people on payroll and the services you want — and the fee is fixed and monthly once agreed, with nothing added afterwards.` },
    { question: `Do I need to be based in ${loc.town} to work with you?`, answer: `Not at all. We support businesses in and around ${loc.town} as well as across the UK. Everything can be handled remotely through Xero and video calls, and we're always happy to meet in person when it's helpful.` },
    { question: "Can you help me switch accountants?", answer: "Yes — switching to CASS is straightforward and can be done at any time of year. We handle the professional handover with your current accountant for you, so there's very little for you to do." },
  ];
  const locFaqs = [...townFaqs, ...SHARED_LOC_FAQS.filter((sf) => !townFaqs.some((f) => f.question.toLowerCase() === sf.question.toLowerCase()))];

  return (
    <div className="bg-white" data-testid={`location-page-${loc.slug}`}>
      <Seo title={loc.metaTitle || `Accountants in ${loc.town}`} description={loc.metaDescription}
        jsonLd={[
          localBusiness,
          breadcrumbSchema([{ name: "Home", path: "/" }, { name: `Accountants in ${loc.town}`, path: `/${loc.slug}` }]),
          ...(locFaqs.length ? [faqSchema(locFaqs)] : []),
        ]} />

      {/* Hero */}
      <section className="bg-navy text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-16 md:py-24">
          <div className="inline-flex items-center gap-2 text-orange font-bold text-sm tracking-widest uppercase mb-4">
            <MapPin size={16} /> {loc.town}, {loc.county}
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight">{loc.h1 || loc.headline}</h1>
          {rating?.rating && (
            <a href={rating.url || rating.write_review_url || "#"} target="_blank" rel="noopener noreferrer"
              data-testid="location-rating-snippet"
              className="inline-flex items-center gap-2 mt-5 bg-white/5 border border-white/10 rounded-full pl-2.5 pr-4 py-1.5 hover:bg-white/10 transition-colors">
              <span className="flex gap-0.5">{[1, 2, 3, 4, 5].map((i) => <Star key={i} size={15} className={i <= Math.round(rating.rating) ? "fill-orange text-orange" : "fill-white/20 text-white/20"} />)}</span>
              <span className="text-sm font-semibold text-white">{rating.rating}/5</span>
              <span className="text-sm text-slate-300">· {rating.total} Google reviews</span>
            </a>
          )}
          <p className="text-slate-300 text-lg mt-6 max-w-2xl leading-relaxed">{heroPara}</p>
          <div className="flex flex-wrap items-center gap-3 mt-8">
            <Link to="/contact" data-testid="location-cta-talk"><Button className="bg-orange hover:bg-orange-dark text-white rounded-full px-8 py-6 text-base font-bold">Let's Talk <ArrowRight size={18} className="ml-2" /></Button></Link>
            <BookCallButton testId="location-book-call" label="Book a Call" className="inline-flex items-center gap-2 border-2 border-white/25 hover:border-white text-white rounded-full px-8 py-[1.15rem] text-base font-bold transition-all bg-transparent" />
            <Link to="/business-health-check" data-testid="location-cta-health"><Button variant="outline" className="rounded-full px-8 py-6 text-base font-bold border-white/25 text-white hover:bg-white/10 bg-transparent">Free Business Health Check</Button></Link>
          </div>
        </div>
      </section>

      {/* Intro continued */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 py-14">
        <p className="text-slate-600 text-lg leading-relaxed max-w-3xl">{bodyParas[0]}</p>
        {bodyParas.slice(1).map((para, i) => (
          <p key={i} className="text-slate-600 text-lg leading-relaxed max-w-3xl mt-5">{para}</p>
        ))}
        <div className="grid sm:grid-cols-2 gap-3 mt-8 max-w-2xl">
          {DIFFERENTIATORS.map((d) => (
            <div key={d} className="flex items-center gap-3 text-navy font-semibold">
              <span className="w-6 h-6 rounded-full bg-green-100 text-green-600 flex items-center justify-center shrink-0"><Check size={14} /></span>
              {d}
            </div>
          ))}
        </div>
      </section>

      {sections.map((s) => <TownSection key={s.heading} s={s} />)}

      {/* More Than Numbers — client outcomes */}
      <section className="bg-navy text-white py-16 md:py-20" data-testid="location-outcomes">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <span className="text-xs font-bold uppercase tracking-widest text-orange">More than numbers</span>
          <h2 className="text-3xl font-extrabold mt-1">What working with CASS means for your {loc.town} business</h2>
          <p className="text-slate-300 mt-4 max-w-2xl leading-relaxed">We don't just keep you compliant. We help you understand your numbers, make better decisions and plan for the future — with fixed monthly fees based on the support you actually need.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-10">
            {OUTCOMES.map(([title, desc]) => (
              <div key={title} className="bg-white/5 rounded-2xl border border-white/10 p-6">
                <div className="flex items-center gap-2 text-orange mb-2"><Check size={18} className="shrink-0" /><h3 className="font-display font-bold text-white text-base">{title}</h3></div>
                <p className="text-sm text-slate-300 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why choose a local Xero accountant (per county). Towns with their own
          sections skip it: on a general accountancy page a "Xero accountant"
          heading competes with the town's own Xero page. */}
      {getCountyCopy(loc.county) && sections.length === 0 && (
        <section className="max-w-5xl mx-auto px-4 sm:px-6 py-16" data-testid="location-county-copy">
          <h2 className="text-2xl md:text-3xl font-extrabold text-navy">{getCountyCopy(loc.county).heading}</h2>
          <p className="text-slate-600 leading-relaxed mt-4 max-w-3xl">{getCountyCopy(loc.county).body}</p>
          <div className="grid sm:grid-cols-3 gap-4 mt-8">
            {getCountyCopy(loc.county).points.map((p) => (
              <div key={p} className="flex items-start gap-2.5 bg-slate-50 rounded-xl border border-slate-100 p-4"><Check size={18} className="text-orange shrink-0 mt-0.5" /><span className="text-sm font-medium text-navy leading-snug">{p}</span></div>
            ))}
          </div>
        </section>
      )}

      {/* The town's sibling pages — kept for every town, including those with
          their own sections, because this is how the Xero and service-by-town
          pages are reached. */}
      {(xeroSlugForTown(loc.slug) || serviceSlugsForTown(loc.slug).length > 0) && (
        <section className="max-w-5xl mx-auto px-4 sm:px-6 pb-4" data-testid="location-sibling-pages">
          {xeroSlugForTown(loc.slug) && (
            <Link to={`/${xeroSlugForTown(loc.slug)}`} data-testid="location-to-xero" className="inline-flex items-center gap-2 text-orange font-bold hover:underline">
              Specifically after Xero support? See Xero accountants in {loc.town} <ArrowRight size={15} />
            </Link>
          )}
          {serviceSlugsForTown(loc.slug).length > 0 && (
            <div className="flex flex-wrap gap-3 mt-6" data-testid="location-service-links">
              {serviceSlugsForTown(loc.slug).map(({ slug, service }) => (
                <Link key={slug} to={`/${slug}`} data-testid={`location-to-${slug}`} className="inline-flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-full px-5 py-2.5 text-sm font-semibold text-navy hover:border-orange hover:-translate-y-0.5 transition-all">
                  {SERVICES[service].label} in {loc.town} <ArrowRight size={14} className="text-orange" />
                </Link>
              ))}
            </div>
          )}
        </section>
      )}

      {/* Services — the generic grid is suppressed where a town names its own,
          so the two lists don't repeat each other. */}
      {!sections.some((s) => (s.items || []).length) && (
      <section className="bg-slate-50 py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <h2 className="text-3xl font-extrabold text-navy">How we help {loc.town} businesses</h2>
          <p className="text-slate-500 mt-2 max-w-2xl">From everyday accounting to strategic finance — everything designed to help your business make better decisions and grow with confidence.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-8">
            {services.slice(0, 9).map((s) => (
              <Link key={s.id} to={s.external_path || `/services/${s.slug}`} data-testid={`location-service-${s.slug}`}
                className="bg-white rounded-xl border border-slate-200 p-4 flex items-center justify-between hover:border-orange hover:-translate-y-0.5 transition-all group">
                <span className="font-semibold text-navy text-sm">{s.title}</span>
                <ArrowRight size={16} className="text-slate-300 group-hover:text-orange" />
              </Link>
            ))}
          </div>
          <div className="mt-8">
            <Link to="/services" className="inline-flex items-center gap-2 text-orange font-bold hover:underline">See all our services <ArrowRight size={16} /></Link>
          </div>
        </div>
      </section>
      )}

      {/* Areas served */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 py-14">
        <h2 className="text-2xl font-extrabold text-navy">Proudly serving {loc.town} & the surrounding area</h2>
        <p className="text-slate-500 mt-2">Including {loc.nearby.slice(0, -1).join(", ")} and {loc.nearby[loc.nearby.length - 1]}.</p>
      </section>

      {/* Local testimonial (written and/or video) — only shown when added in the admin */}
      {(testimonial?.quote || testimonial?.video_url) && (
        <section className="bg-slate-50 py-16" data-testid="location-testimonial">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
            {testimonial.video_url && (
              <div className="rounded-2xl overflow-hidden border border-slate-200 shadow-sm bg-black aspect-video mb-8" data-testid="location-testimonial-video">
                <video src={mediaUrl(testimonial.video_url)} poster={mediaUrl(`${testimonial.video_url}/poster`)} className="w-full h-full" controls preload="metadata" />
              </div>
            )}
            {testimonial.quote && (
              <>
                <Quote size={40} className="text-orange/30 mx-auto" />
                <blockquote className="text-xl md:text-2xl font-display font-bold text-navy leading-snug mt-5">
                  &ldquo;{testimonial.quote}&rdquo;
                </blockquote>
                <div className="flex items-center justify-center gap-1 mt-6">
                  {[1, 2, 3, 4, 5].map((i) => <Star key={i} size={18} className="fill-orange text-orange" />)}
                </div>
              </>
            )}
            {(testimonial.author || testimonial.role || testimonial.location) && (
              <p className="text-sm font-semibold text-slate-500 mt-3" data-testid="location-testimonial-author">
                — {testimonial.author}{testimonial.role ? `, ${testimonial.role}` : ""}{testimonial.location ? ` · Based in ${testimonial.location}` : ""}
              </p>
            )}
          </div>
        </section>
      )}

      {/* Real Google reviews (firm-wide), rotated per town for variety */}
      <GoogleReviewsSection
        testId="location-reviews"
        eyebrow="Trusted locally"
        excludeAuthor={testimonial?.author || ""}
        offset={Math.max(0, LOCATIONS.findIndex((l) => l.slug === loc.slug)) * 2}
      />

      {/* Local FAQs */}
      {locFaqs.length > 0 && (
        <section className="max-w-4xl mx-auto px-4 sm:px-6 py-16" data-testid="location-faqs">
          <div className="flex items-center gap-3 mb-8">
            <span className="w-11 h-11 rounded-xl bg-orange/10 flex items-center justify-center text-orange shrink-0"><HelpCircle size={22} /></span>
            <div>
              <span className="text-xs font-bold uppercase tracking-widest text-orange">Accountants in {loc.town}</span>
              <h2 className="text-2xl md:text-3xl font-extrabold text-navy leading-tight">Frequently asked questions</h2>
            </div>
          </div>
          <Accordion type="single" collapsible className="space-y-3">
            {locFaqs.map((f, i) => (
              <AccordionItem key={i} value={`loc-faq-${i}`} data-testid={`location-faq-${i}`} className="bg-slate-50 rounded-xl border border-slate-100 px-5">
                <AccordionTrigger className="text-left font-display font-bold text-navy hover:no-underline">{f.question}</AccordionTrigger>
                <AccordionContent className="text-slate-600 leading-relaxed">{f.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>
      )}

      {/* The answer library: the town page keeps its local questions, the
          national ones live in one place and are linked to. */}
      <AskCassieRelated
        topic="bookkeeping payroll VAT management accounts switching accountants outsourced finance"
        heading={`What owners in ${loc.town} ask us`}
        limit={5}
      />

      {/* Related towns — internal linking for local SEO */}
      {(() => {
        const others = LOCATIONS.filter((l) => l.slug !== loc.slug);
        if (others.length === 0) return null;
        return (
          <section className="max-w-5xl mx-auto px-4 sm:px-6 py-14" data-testid="location-related-towns">
            <h2 className="text-2xl font-extrabold text-navy">Accountants in nearby towns</h2>
            <p className="text-slate-500 mt-2">We support businesses right across the Midlands. Explore our other local pages.</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-8">
              {others.map((l) => (
                <Link key={l.slug} to={`/${l.slug}`} data-testid={`related-town-${l.slug}`}
                  className="bg-white rounded-xl border border-slate-200 p-4 flex items-center justify-between hover:border-orange hover:-translate-y-0.5 transition-all group">
                  <span className="inline-flex items-center gap-2 font-semibold text-navy text-sm"><MapPin size={15} className="text-orange" /> Accountants in {l.town}</span>
                  <ArrowRight size={16} className="text-slate-300 group-hover:text-orange" />
                </Link>
              ))}
            </div>
          </section>
        );
      })()}

      {/* CTA */}
      <section className="bg-navy text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-16 text-center">
          <h2 className="text-3xl sm:text-4xl font-extrabold">Ready to talk to an accountant in {loc.town}?</h2>
          <p className="text-slate-300 mt-4 max-w-xl mx-auto">Book a free, no-obligation chat. We'll learn about your business and show you exactly how we can help.</p>
          <div className="flex flex-wrap items-center justify-center gap-3 mt-8">
            <Link to="/contact"><Button className="bg-orange hover:bg-orange-dark text-white rounded-full px-8 py-6 text-base font-bold">Let's Talk <ArrowRight size={18} className="ml-2" /></Button></Link>
            <BookCallButton testId="location-book-call-cta" label="Book a Call" className="inline-flex items-center gap-2 border-2 border-white/25 hover:border-white text-white rounded-full px-8 py-[1.15rem] text-base font-bold transition-all bg-transparent" />
            <a href="tel:+441827780550"><Button variant="outline" className="rounded-full px-8 py-6 text-base font-bold border-white/25 text-white hover:bg-white/10 bg-transparent"><Phone size={16} className="mr-2" /> 01827 780550</Button></a>
          </div>
        </div>
      </section>
    </div>
  );
}
